import { Link } from '@remix-run/react';
import styled, { css } from 'styled-components';
import ArrowSubmit from '~/components/icons/ArrowSubmit';
import { fontFamilies } from '~/theme/typography';

// Form presentation shared by every card inside `CampaignScreen`: the private-sale
// sign-up form, the private-sale password gate, and `/signup` (a different campaign on
// a different mailing list — see that component's docblock). Calibrated for the CARD
// backdrop — mutedOnDark/errorOnDark, not greyDisabled2/error, which fail AA there.
// See specs/private-sale-sign-up.md.

// A `css` helper as well as `CardForm` below, because the gate is a `styled(Form)` from
// Remix while the two sign-up bodies are plain `styled.form` — they cannot share one
// base element, so the helper is what the odd one out interpolates.
export const cardFormLayout = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const CardCopy = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 14px;
  line-height: normal;
  letter-spacing: 0.5px;
  text-align: center;
  text-transform: uppercase;
  margin: 0;
`;

export const CardSubtitle = styled(CardCopy)`
  line-height: 1.4;
`;

/**
 * Groups the hint with its input on a tighter gap than `cardFormLayout`'s, so the two
 * read as one control rather than as another line of copy.
 */
export const CardFieldGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

/**
 * The field instruction. A real `<label>` (wire it with `htmlFor` and leave `aria-label`
 * OFF the input, which would override it for screen readers) because the copy is too
 * long to live in the placeholder — it clips mid-word at 375px.
 *
 * Deliberately smaller and muted rather than reusing `CardSubtitle`: at the subtitle's
 * size and colour it read as a third line of marketing copy instead of as a hint
 * attached to the field. `mutedOnDark` is 6.78:1 on the card's worst-case composite, so
 * it still clears AA at 11px.
 */
export const CardFieldHint = styled.label`
  color: ${({ theme }) => theme.colors.mutedOnDark};
  font-family: ${fontFamilies.CircularStd};
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 0.03125rem;
  text-align: center;
  text-transform: uppercase;
  margin: 0;
`;

export const CardField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 52px;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  background: transparent;
  box-sizing: border-box;
`;

export const CardInput = styled.input`
  flex: 1;
  align-self: stretch;
  min-width: 0;
  background: transparent;
  -webkit-user-select: text;
  border: none;
  outline: none;
  box-shadow: none;
  padding: 0;
  font-family: ${fontFamilies.CircularStd};
  font-size: 16px;
  font-weight: 450;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};

  /* :focus-visible is folded in here deliberately, and the previous comment claiming it had
     to stay separate was wrong on the facts. Browsers apply a UA heuristic in which an
     element that expects keyboard input — which input[type=text|password|email] does —
     matches :focus-visible on *pointer* focus as well as keyboard focus. So a separate
     :focus-visible outline is not a keyboard-only affordance on this element: it draws a
     ring on every click, and the :focus rule below could never win against it. Splitting
     them bought nothing and shipped a ring the design does not want.

     What remains as the focus affordance is CardField's border-bottom plus the caret. That
     is weaker than a WCAG 2.4.7 focus indicator, and it is a knowing trade — it matches the
     three other inputs in this app (sections/signup, SignupModal, EventPasswordForm), all
     of which suppress the ring the same way. If an indicator is ever reinstated it must be
     a CardField change on :focus-within (a brighter or thicker underline, say), not an
     outline on the input, or this regresses. */
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    border: none;
    box-shadow: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.mutedOnDark};
    font-family: ${fontFamilies.CircularStd};
    font-size: 16px;
    font-weight: 450;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CardSubmitButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  /* Disabled is the resting state, so the theme's dim grey would leave no visible
     affordance on the card. The arrow inherits via currentColor. */
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.mutedOnDark};
  }
`;

// No colour and no props: the SVG strokes with `currentColor` so it follows
// CardSubmitButton, and `shouldForwardProp` in root.tsx only filters string targets —
// a prop passed here would be spread onto the DOM svg.
export const CardArrowIcon = styled(ArrowSubmit)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

export const CardErrorMessage = styled(CardCopy)`
  color: ${({ theme }) => theme.colors.errorOnDark};
`;

// The plain-`<form>` base for the two sign-up bodies. The gate interpolates
// `cardFormLayout` into a `styled(Form)` instead; see the note at the top.
export const CardForm = styled.form`
  ${cardFormLayout}
`;

// Copy BELOW the form — consent text, the gate's "no password?" prompt — as opposed to
// `CardFieldHint`, which is a `<label>` attached to a control. Smaller and looser than
// `CardSubtitle` so it reads as fine print rather than another marketing line.
export const CardFinePrint = styled(CardCopy)`
  font-size: 11px;
  line-height: 1.5;
  letter-spacing: 0.03125rem;
`;

// Inline link inside `CardFinePrint`. Underlined rather than coloured: the card sits on
// scrimmed artwork, and no accent in the theme clears AA against it.
export const CardLink = styled(Link)`
  color: ${({ theme }) => theme.colors.white};
  text-decoration: underline;
`;
